import { FC } from 'react'
import classNames from 'classnames'
import { Controller, useFormContext } from 'react-hook-form'
import { t } from 'i18next'

import { Popover } from '@lounge-fe/ui-kit/src/components/Popover'
import { Transition } from '@lounge-fe/ui-kit/src/components/Transition'
import { Button } from '@lounge-fe/ui-kit/src/components/Button'
import { Icon } from '@lounge-fe/ui-kit/src/components/Icon'
import { Text } from '@lounge-fe/ui-kit/src/components/Text'
import { useMediaQuery } from '@lounge-fe/ui-kit/src/hooks'
import { Portal } from '@lounge-fe/ui-kit/src/components/Portal'
import { CloseIconButton } from '@lounge-fe/ui-kit/src/components/CloseIconButton'
import { RadioGroup } from '@lounge-fe/ui-kit/src/components/RadioGroup'
import { CollectionsSearchRequest, CollectionsValues } from '../../types'

import { SortByProps } from '../types'

export const SortBy: FC<SortByProps> = ({ name, options }) => {
  const { control } = useFormContext<CollectionsValues>()
  const isDesktop = useMediaQuery(({ screens }) => `(min-width: ${screens.md})`)

  return (
    <div
      className="
        relative flex flex-1 whitespace-nowrap items-center
        md:flex-grow-0 md:border-l md:border-primary-default"
    >
      <Popover scrollLock={!isDesktop}>
        {({ isOpen, onClose }) => (
          <>
            <Popover.Trigger>
              <Button
                className="w-full h-full rounded-[0] pr-2 hover:enabled:bg-transparent normal-case typography typography--body-sm"
                variant="ghost"
                endIcon={
                  <Icon.ChevronDown
                    className={classNames(
                      'w-2 transition',
                      isOpen && 'rotate-180',
                    )}
                  />
                }
              >
                {t('action.sort_by')}
              </Button>
            </Popover.Trigger>
            {isDesktop ? (
              <Transition.Fade
                className="
                  absolute top-full right-2 py-5 px-4 z-10
                  shadow-2 bg-white"
              >
                <Popover.Content
                  onClickAway={(e) => {
                    onClose()
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                >
                  <Controller
                    name={name}
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        value={field.value ?? ''}
                        onChange={(...args) => {
                          field.onChange(...args)
                          onClose()
                        }}
                      >
                        {options.map((option) => (
                          <RadioGroup.Item key={option.value} {...option} />
                        ))}
                      </RadioGroup>
                    )}
                  />
                </Popover.Content>
              </Transition.Fade>
            ) : (
              <Portal>
                <Popover.Backdrop className="z-40" />
                <Transition.Slide
                  direction="bottom"
                  className={classNames(
                    'fixed bottom-0 w-full w-full bg-white z-50',
                    'shadow-2 rounded-t-sm',
                  )}
                >
                  <Popover.Content className="pb-7">
                    <div className="py-4 px-6 flex justify-between items-center border-b border-primary-default">
                      <Text variant="title-six" className="text-md">
                        {t('action.sort_by')}
                      </Text>
                      <CloseIconButton onClick={onClose} aria-label="Close" />
                    </div>
                    <Controller
                      name={name}
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          {...field}
                          value={field.value ?? ''}
                          onChange={(...args) => {
                            field.onChange(...args)
                            onClose()
                          }}
                        >
                          {options.map((option) => (
                            <RadioGroup.Item
                              className="py-5 mx-6 border-b border-primary-default last:border-0"
                              key={option.value}
                              {...option}
                            />
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </Popover.Content>
                </Transition.Slide>
              </Portal>
            )}
          </>
        )}
      </Popover>
    </div>
  )
}
